import { assertWritable } from './backendConfig';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: unknown = undefined,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export type CursorPage<T> = {
  data: T[];
  nextCursor: string | null;
  hasMore: boolean;
};

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body && !headers.has('Content-Type'))
    headers.set('Content-Type', 'application/json');
  const response = await fetch(path, {
    ...init,
    headers,
    credentials: 'include',
  });
  const text = await response.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : undefined;
  } catch {
    body = text;
  }
  if (!response.ok) {
    const message =
      typeof body === 'object' && body !== null && 'message' in body
        ? String(body.message)
        : `API request failed (${response.status})`;
    throw new ApiError(message, response.status, body);
  }
  return body as T;
}

export function get<T>(path: string): Promise<T> {
  return request<T>(path);
}
export function post<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body: JSON.stringify(body) });
}
export function put<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'PUT', body: JSON.stringify(body) });
}
export function patch<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
}
export function del<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'DELETE' });
}

export async function mutate<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('X-CSRF-TOKEN', await getCsrfToken());
  let result: T;
  try {
    result = await request<T>(path, { ...init, headers });
  } catch (error) {
    // A login/session rotation can invalidate the cached token; refresh once.
    if (error instanceof ApiError && error.status === 419) {
      csrfToken = undefined;
      headers.set('X-CSRF-TOKEN', await getCsrfToken());
      result = await request<T>(path, { ...init, headers });
    } else {
      throw error;
    }
  }
  // Let the app refresh its local data immediately after a successful write
  // (registered by the trip store), instead of waiting for the next sync poll.
  onMutationApplied?.();
  return result;
}

// Registered by the store on init. Kept registration-based (no import of the
// store here) to avoid a circular dependency between apiClient and data/store.
let onMutationApplied: (() => void) | undefined;
export function setMutationAppliedHandler(
  handler: (() => void) | undefined,
): void {
  onMutationApplied = handler;
}

export function postMutation<T>(path: string, body: unknown): Promise<T> {
  assertWritable('this operation');
  return mutate<T>(path, { method: 'POST', body: JSON.stringify(body) });
}

export function putMutation<T>(path: string, body: unknown): Promise<T> {
  assertWritable('this operation');
  return mutate<T>(path, { method: 'PUT', body: JSON.stringify(body) });
}

export function patchMutation<T>(path: string, body: unknown): Promise<T> {
  assertWritable('this operation');
  return mutate<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
}

export function deleteMutation<T>(path: string): Promise<T> {
  assertWritable('this operation');
  return mutate<T>(path, { method: 'DELETE' });
}

let csrfToken: string | undefined;

export async function getCsrfToken(): Promise<string> {
  csrfToken ??= (await get<{ token: string }>('/api/csrf-token')).token;
  return csrfToken;
}

export { request as apiRequest };
